// Subtitle style settings — one independent style set per rendered video:
//   • Video 1 "ES + EN"  → es line (Spanish) + en line (English translation)
//   • Video 2 "RU"        → ru line (Russian)
// A live 9:16 preview (over a real episode screenshot) shows every change, and
// Save persists the v2 payload that both the editor preview and the libass
// renderer read back.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useStore } from '../state/store.js';
import {
  defaultSettings,
  normalizeSettings,
  assToHex,
  hexToAss,
  normalizeAssColor,
  previewLineStyle,
  previewBoxStyle,
  resolveEnPosY,
  STYLE_PRESETS,
} from '../lib/captionSettings.js';

const SAMPLE = {
  es: 'El espíritu de la selva\nnos acompaña hoy',
  en: 'The spirit of the jungle\nis with us today',
  ru: 'Дух джунглей\nсегодня с нами',
};

export function SettingsScreen() {
  const navigate = useNavigate();
  const toast = useStore((s) => s.toast);

  const [settings, setSettings] = useState(null);
  const [fonts, setFonts] = useState([]);
  const [activeVideo, setActiveVideo] = useState('esen'); // 'esen' | 'ru'
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [sample, setSample] = useState(SAMPLE);

  useEffect(() => {
    let alive = true;
    api.getSettings()
      .then((raw) => { if (alive) setSettings(normalizeSettings(raw)); })
      .catch((err) => { if (alive) { setSettings(defaultSettings()); toast(err.message, 'err'); } });
    api.getFonts()
      .then((r) => { if (alive) setFonts(r?.fonts || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [toast]);

  const patchLine = (lineKey, patch) => {
    setSettings((cur) => ({ ...cur, [lineKey]: { ...cur[lineKey], ...patch } }));
    setDirty(true);
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await api.updateSettings(settings);
      setDirty(false);
      toast('настройки сохранены — превью и рендер обновлены', 'ok');
    } catch (err) {
      toast(err.message, 'err');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    setSettings(defaultSettings());
    setDirty(true);
    toast('сброшено к стандартным — не забудь сохранить', 'info');
  };

  const applyPreset = (preset) => {
    setSettings((cur) => normalizeSettings(preset.apply(cur)));
    setDirty(true);
    toast(`пресет «${preset.label}» применён — не забудь сохранить`, 'info');
  };

  if (!settings) {
    return (
      <div className="screen">
        <div className="empty-state"><div className="empty-icon">◇</div><div className="empty-title">загрузка настроек…</div></div>
      </div>
    );
  }

  return (
    <div className="screen settings-screen">
      <div className="breadcrumb breadcrumb-oneline" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className="back"
          onClick={() => {
            // Settings usually opens in its own tab (from the editor). Closing it
            // returns focus to the editor; otherwise fall back to a real route
            // (navigate(-1) does nothing in a fresh tab — that was the broken case).
            if (window.opener && !window.opener.closed) { window.close(); return; }
            if (window.history.length > 1) navigate(-1);
            else navigate('/episodes');
          }}
        >← назад</button>
        <span className="crumb-sep">/</span>
        <span className="crumb-current crumb-strong">Стиль субтитров</span>
        {dirty && <span className="chip chip-soft">есть несохранённые изменения</span>}
      </div>

      <div className="settings-presets">
        <span className="settings-presets-label">Пресеты:</span>
        {STYLE_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            className="btn btn-ghost btn-compact"
            title={p.hint}
            onClick={() => applyPreset(p)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="settings-layout">
        {/* LEFT — live preview */}
        <div className="settings-preview-col">
          <div className="settings-video-tabs">
            <button
              type="button"
              className={'btn btn-compact ' + (activeVideo === 'esen' ? 'btn-dark' : 'btn-ghost')}
              onClick={() => setActiveVideo('esen')}
            >
              Видео 1 · ES + EN
            </button>
            <button
              type="button"
              className={'btn btn-compact ' + (activeVideo === 'ru' ? 'btn-dark' : 'btn-ghost')}
              onClick={() => setActiveVideo('ru')}
            >
              Видео 2 · RU
            </button>
          </div>
          <SubtitlePreview settings={settings} video={activeVideo} sample={sample} />
          <div className="settings-preview-hint">
            Фон — кадр из эпизода. Текст образца можно менять — это превью, не данные клипа.
          </div>
          <div className="settings-sample-edit">
            {activeVideo === 'esen' ? (
              <>
                <SampleField label="Образец ES" value={sample.es} onChange={(es) => setSample((s) => ({ ...s, es }))} />
                <SampleField label="Образец EN" value={sample.en} onChange={(en) => setSample((s) => ({ ...s, en }))} />
              </>
            ) : (
              <SampleField label="Образец RU" value={sample.ru} onChange={(ru) => setSample((s) => ({ ...s, ru }))} />
            )}
          </div>
        </div>

        {/* RIGHT — controls for the active video */}
        <div className="settings-controls-col">
          {activeVideo === 'esen' ? (
            <>
              <LineEditor
                title="Испанский (основная строка)"
                line={settings.es}
                fonts={fonts}
                onChange={(p) => patchLine('es', p)}
              />
              <LineEditor
                title="Английский (перевод)"
                line={settings.en}
                fonts={fonts}
                withOpacity
                gapMode
                onChange={(p) => patchLine('en', p)}
              />
            </>
          ) : (
            <LineEditor
              title="Русский"
              line={settings.ru}
              fonts={fonts}
              onChange={(p) => patchLine('ru', p)}
            />
          )}

          <div className="settings-actions">
            <button type="button" className="btn btn-ghost" onClick={resetDefaults}>сбросить</button>
            <button type="button" className="btn btn-dark" onClick={save} disabled={saving || !dirty}>
              {saving ? 'сохранение…' : 'сохранить'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Live 9:16 preview --------------------------------------------------

function SubtitlePreview({ settings, video, sample = SAMPLE }) {
  const boxRef = useRef(null);
  const [boxHeight, setBoxHeight] = useState(0);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const sync = () => setBoxHeight(el.clientHeight);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="settings-preview-box" ref={boxRef}>
      <img className="settings-preview-bg" src="/preview-bg.jpg" alt="" />
      {video === 'esen' ? (
        <>
          <PreviewLine line={settings.es} text={sample.es} boxHeight={boxHeight} anchor="bottom" />
          <PreviewLine line={settings.en} text={sample.en} boxHeight={boxHeight} posY={resolveEnPosY(settings)} anchor="top" />
        </>
      ) : (
        <PreviewLine line={settings.ru} text={sample.ru} boxHeight={boxHeight} anchor="bottom" />
      )}
    </div>
  );
}

// One positioned caption line with an optional background plate hugging the text.
function PreviewLine({ line, text, boxHeight, posY, anchor }) {
  return (
    <div style={previewLineStyle(line, { boxHeight, posY, anchor })}>
      <span style={previewBoxStyle(line)}>{text}</span>
    </div>
  );
}

function SampleField({ label, value, onChange }) {
  return (
    <label className="settings-sample-field">
      <span className="settings-field-label">{label}</span>
      <input
        className="settings-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="текст образца…"
      />
    </label>
  );
}

// ---- Per-line control group --------------------------------------------

function LineEditor({ title, line, fonts, onChange, withOpacity, gapMode }) {
  return (
    <div className="ed-card settings-card">
      <div className="ed-card-head"><div className="ed-card-title">{title}</div></div>

      <div className="settings-grid">
        <Field label="Шрифт">
          <select
            className="settings-input"
            value={line.fontFamily}
            onChange={(e) => onChange({ fontFamily: e.target.value })}
          >
            {[...new Set([line.fontFamily, ...fonts])].filter(Boolean).map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </Field>

        <SliderField
          label="Размер"
          value={line.fontSize}
          min={24}
          max={120}
          step={1}
          onChange={(fontSize) => onChange({ fontSize })}
        />

        {gapMode ? (
          <SliderField
            label="Отступ от испанской"
            value={line.gap ?? 0.06}
            min={-0.05}
            max={0.2}
            step={0.0025}
            format={(v) => `${(v * 100).toFixed(2)}%`}
            onChange={(gap) => onChange({ gap })}
          />
        ) : (
          <SliderField
            label="Позиция по вертикали"
            value={line.posY}
            min={0.5}
            max={0.95}
            step={0.005}
            format={(v) => `${Math.round(v * 100)}%`}
            onChange={(posY) => onChange({ posY })}
          />
        )}

        <AlignField
          value={line.align ?? 'center'}
          onChange={(align) => onChange({ align })}
        />

        <SliderField
          label="Обводка"
          value={line.outlineWidth}
          min={0}
          max={8}
          step={0.5}
          onChange={(outlineWidth) => onChange({ outlineWidth })}
        />

        <SliderField
          label="Тень"
          value={line.shadowWidth}
          min={0}
          max={10}
          step={0.5}
          onChange={(shadowWidth) => onChange({ shadowWidth })}
        />

        <SliderField
          label="Межстрочный"
          value={line.lineHeight}
          min={0.9}
          max={2}
          step={0.05}
          onChange={(lineHeight) => onChange({ lineHeight })}
        />

        <SliderField
          label="Межбуквенный"
          value={line.letterSpacing}
          min={0}
          max={10}
          step={0.5}
          onChange={(letterSpacing) => onChange({ letterSpacing })}
        />

        {withOpacity && (
          <SliderField
            label="Прозрачность"
            value={line.opacity ?? 1}
            min={0.1}
            max={1}
            step={0.05}
            format={(v) => `${Math.round(v * 100)}%`}
            onChange={(opacity) => onChange({ opacity })}
          />
        )}

        <Field label="Цвет текста">
          <input type="color"
                 className="settings-color"
                 value={assToHex(line.primaryColor, '#ffffff')}
                 onChange={(e) => onChange({
                   primaryColor: normalizeAssColor(e.target.value, '&H00FFFFFF'),
                 })} />
        </Field>

        <Field label="Цвет обводки">
          <input type="color"
                 className="settings-color"
                 value={assToHex(line.outlineColor, '#000000')}
                 onChange={(e) => onChange({
                   outlineColor: normalizeAssColor(e.target.value, '&H00000000'),
                 })} />
        </Field>
      </div>

      <div className="settings-toggles">
        <label className="settings-toggle">
          <input type="checkbox" checked={!!line.bold}
                 onChange={(e) => onChange({ bold: e.target.checked })} />
          Жирный
        </label>
        <label className="settings-toggle">
          <input type="checkbox" checked={!!line.italic}
                 onChange={(e) => onChange({ italic: e.target.checked })} />
          Курсив
        </label>
        <label className="settings-toggle">
          <input type="checkbox" checked={!!line.useBox}
                 onChange={(e) => onChange({ useBox: e.target.checked })} />
          Подложка
        </label>
      </div>

      {line.useBox && (
        <div className="settings-grid settings-box-grid">
          <Field label="Цвет подложки">
            <input type="color"
                   className="settings-color"
                   value={assToHex(line.boxColor, '#000000')}
                   onChange={(e) => onChange({
                     boxColor: normalizeAssColor(e.target.value, '&H00000000'),
                   })} />
          </Field>
          <SliderField
            label="Плотность подложки"
            value={line.boxOpacity ?? 0.55}
            min={0.1}
            max={1}
            step={0.05}
            format={(v) => `${Math.round(v * 100)}%`}
            onChange={(boxOpacity) => onChange({ boxOpacity })}
          />
        </div>
      )}
    </div>
  );
}

function AlignField({ value, onChange }) {
  const opts = [
    ['left', '⬅ слева'],
    ['center', '⬌ центр'],
    ['right', 'справа ➡'],
  ];
  return (
    <Field label="Выравнивание">
      <div className="settings-align-row">
        {opts.map(([v, label]) => (
          <button
            key={v}
            type="button"
            className={'btn btn-compact ' + (value === v ? 'btn-dark' : 'btn-ghost')}
            onClick={() => onChange(v)}
          >
            {label}
          </button>
        ))}
      </div>
    </Field>
  );
}

function Field({ label, children }) {
  return (
    <label className="settings-field">
      <span className="settings-field-label">{label}</span>
      {children}
    </label>
  );
}

function clampNum(value, min, max, step = 1) {
  let v = Number(value);
  if (Number.isNaN(v)) return null;
  if (step > 0) v = Math.round(v / step) * step;
  return Math.min(max, Math.max(min, v));
}

function SliderField({ label, value, min, max, step, onChange, format }) {
  const display = format ? format(value) : value;
  const commit = (raw) => {
    const v = clampNum(raw, min, max, step);
    if (v != null) onChange(v);
  };

  return (
    <Field label={`${label} · ${display}`}>
      <div className="settings-slider-row">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
        />
        <input
          type="number"
          className="settings-num-input"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => commit(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
        />
      </div>
    </Field>
  );
}
